import React, { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { SEO } from '../components/SEO';

interface InvestmentState {
  initialAmount: string;
  monthlyContribution: string;
  years: string;
  interestRate: string;
  compoundingFrequency: 'monthly' | 'quarterly' | 'annually';
  currency: 'EUR' | 'BAM' | 'RSD';
}

interface Results {
  totalContributions: number;
  totalInterest: number;
  finalBalance: number;
  yearlyBreakdown: {
    year: number;
    contributions: number;
    interest: number;
    balance: number;
  }[];
}

const currencySymbols: Record<string, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

function InvesticijskiKalkulator() {
  const [values, setValues] = useState<InvestmentState>({
    initialAmount: '',
    monthlyContribution: '',
    years: '',
    interestRate: '',
    compoundingFrequency: 'monthly',
    currency: 'EUR'
  });

  const [results, setResults] = useState<Results | null>(null);

  const calculateInvestment = () => {
    const principal = parseFloat(values.initialAmount) || 0;
    const monthlyContribution = parseFloat(values.monthlyContribution) || 0;
    const years = parseFloat(values.years) || 0;
    const annualRate = (parseFloat(values.interestRate) || 0) / 100;
    
    let periodsPerYear: number;
    switch (values.compoundingFrequency) {
      case 'monthly':
        periodsPerYear = 12;
        break;
      case 'quarterly':
        periodsPerYear = 4;
        break;
      case 'annually':
        periodsPerYear = 1;
        break;
      default:
        periodsPerYear = 12;
    }

    const totalPeriods = years * periodsPerYear;
    const ratePerPeriod = annualRate / periodsPerYear;
    const contributionPerPeriod = monthlyContribution * (12 / periodsPerYear);

    let balance = principal;
    let totalContributions = principal;
    const yearlyBreakdown = [];

    for (let year = 1; year <= years; year++) {
      let yearlyContributions = 0;
      let yearlyInterest = 0;
      let previousBalance = balance;

      for (let period = 1; period <= periodsPerYear; period++) {
        // Add contribution
        balance += contributionPerPeriod;
        yearlyContributions += contributionPerPeriod;
        
        // Calculate interest
        const periodInterest = balance * ratePerPeriod;
        balance += periodInterest;
        yearlyInterest += periodInterest;
      }

      totalContributions += yearlyContributions;
      yearlyBreakdown.push({
        year,
        contributions: yearlyContributions,
        interest: yearlyInterest,
        balance: balance
      });
    }

    return {
      totalContributions: parseFloat(totalContributions.toFixed(2)),
      totalInterest: parseFloat((balance - totalContributions).toFixed(2)),
      finalBalance: parseFloat(balance.toFixed(2)),
      yearlyBreakdown
    };
  };

  const handleInputChange = (field: keyof InvestmentState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.initialAmount && values.years && values.interestRate) {
      const results = calculateInvestment();
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Investicijski Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte potencijalni povrat na vaše investicije. Uključuje složeni kamatni račun i mjesečne doprinose za dugoročno planiranje.",
    "url": "https://online-kalkulator.com/investicijski-kalkulator",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun složenog kamatnog računa",
      "Mjesečni doprinosi",
      "Godišnji pregled investicija",
      "Viševalutna podrška",
      "Detaljni izvještaji"
    ]
  };

  return (
    <>
      <SEO 
        title="Investicijski Kalkulator | Izračun Povrata na Investicije"
        description="Izračunajte potencijalni povrat na vaše investicije. Uključuje složeni kamatni račun i mjesečne doprinose za dugoročno planiranje."
        canonicalUrl="/investicijski-kalkulator"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <TrendingUp className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Investicijski Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Početni iznos
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.initialAmount}
                      onChange={(e) => handleInputChange('initialAmount', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite iznos u ${values.currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {currencySymbols[values.currency]}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mjesečni doprinos
                  </label>
                  <input
                    type="number"
                    value={values.monthlyContribution}
                    onChange={(e) => handleInputChange('monthlyContribution', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mjesečna uplata"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Razdoblje (godine)
                  </label>
                  <input
                    type="number"
                    value={values.years}
                    onChange={(e) => handleInputChange('years', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Godišnja kamatna stopa (%)
                  </label>
                  <input
                    type="number"
                    value={values.interestRate}
                    onChange={(e) => handleInputChange('interestRate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 7"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Obračun kamata
                  </label>
                  <select
                    value={values.compoundingFrequency}
                    onChange={(e) => handleInputChange('compoundingFrequency', e.target.value as InvestmentState['compoundingFrequency'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="monthly">Mjesečno</option>
                    <option value="quarterly">Kvartalno</option>
                    <option value="annually">Godišnje</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valuta
                  </label>
                  <select
                    value={values.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value as InvestmentState['currency'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="EUR">Euro (€)</option>
                    <option value="BAM">Konvertibilna marka (KM)</option>
                    <option value="RSD">Srpski dinar (RSD)</option>
                  </select>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-6 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupno uloženo</div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatCurrency(results.totalContributions)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupna kamata</div>
                      <div className="text-xl font-bold text-green-600">
                        {formatCurrency(results.totalInterest)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Konačni iznos</div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatCurrency(results.finalBalance)} {currencySymbols[values.currency]}
                      </div>
                    </div>
                  </div>

                  {/* Yearly Breakdown */}
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Godišnji pregled</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Godina
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Doprinosi
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Kamata
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Stanje
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {results.yearlyBreakdown.map((year) => (
                            <tr key={year.year}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {year.year}.
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(year.contributions)} {currencySymbols[values.currency]}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(year.interest)} {currencySymbols[values.currency]}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(year.balance)} {currencySymbols[values.currency]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Investicijskom Kalkulatoru</h2>
                <p className="text-gray-600 leading-relaxed">
                  Investicijski kalkulator je moćan alat koji vam pomaže planirati i vizualizirati rast
                  vaših investicija tijekom vremena. Uzima u obzir početni ulog, redovite mjesečne
                  doprinose i složeni kamatni račun za precizan izračun potencijalnog povrata.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Radi Složeni Kamatni Račun</h2>
                <p className="text-gray-600 mb-4">
                  Složeni kamatni račun je proces u kojem se kamata obračunava ne samo na početni ulog,
                  već i na prethodno zarađenu kamatu. Ovo stvara efekt "kamata na kamatu" koji može
                  značajno povećati vaše investicije tijekom vremena.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Mjesečni obračun: Kamata se obračunava 12 puta godišnje</li>
                  <li>Kvartalni obračun: Kamata se obračunava 4 puta godišnje</li>
                  <li>Godišnji obračun: Kamata se obračunava jednom godišnje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite početni iznos koji želite uložiti</li>
                  <li>Odredite mjesečni iznos koji ćete dodatno ulagati</li>
                  <li>Postavite razdoblje ulaganja u godinama</li>
                  <li>Unesite očekivanu godišnju kamatnu stopu</li>
                  <li>Odaberite učestalost obračuna kamata</li>
                  <li>Pregledajte rezultate i godišnji pregled</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Ne uključuju inflaciju i porezne obveze</li>
                  <li>Pretpostavljaju konstantnu kamatnu stopu</li>
                  <li>Stvarni povrati mogu odstupati od izračuna</li>
                  <li>Konzultirajte financijskog savjetnika za osobni plan</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faktori koji Utječu na Povrat</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Vrijeme ulaganja (duže razdoblje = veći potencijalni povrat)</li>
                  <li>Učestalost doprinosa (redoviti doprinosi povećavaju konačni iznos)</li>
                  <li>Kamatna stopa (viša stopa = brži rast, ali i veći rizik)</li>
                  <li>Učestalost obračuna kamata (češći obračun = malo bolji povrat)</li>
                  <li>Tržišni uvjeti i ekonomski faktori</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Investiranje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Počnite ulagati što ranije zbog efekta složenog kamatnog računa</li>
                  <li>Redovito doprinosite svojim investicijama</li>
                  <li>Diverzificirajte svoj investicijski portfelj</li>
                  <li>Budite svjesni rizika i svojih investicijskih ciljeva</li>
                  <li>Redovito pregledavajte i prilagođavajte svoju strategiju</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default InvesticijskiKalkulator;